import CashFlowBuilder from "../cash-flow/CashFlowBuilder.tsx";
import CashFlowReportPreview from "../cash-flow/CashFlowReportPreview.tsx";
import { ReportType } from "../../../../../../../api/types/svTypes.ts";
import {
  selectReportType,
  selectTabularFields,
  selectTabularSettingsState,
} from "../../../store/selectors/fieldsStateSelectors.ts";
import { selectDimensions } from "../../../store/selectors/metaDataSelectors.ts";
import useRequestConfigBuilder from "./hooks/useRequestConfigBuilder.ts";
import { useBiApiClientProvider } from "../../../contexts/BiApiClientProviderContext.ts";
import type { BiApiClient } from "../../../contexts/ApiClientProviderContext.ts";
import { getTabularExportWarning } from "../../common/export/exportWarnings.ts";
import ExcelReportExport from "../../common/export/ExcelReportExport.tsx";
import { Box, Stack } from "@mui/material";
import { useSelector } from "react-redux";
import { useBiCommonDataContext } from "../../../contexts/BiCommonDataContext.ts";
import PartContainer from "../../viewer/common/PartContainer.tsx";
import PreviewActions from "../../viewer/common/PreviewActions.tsx";
import EditingHistoryControls from "../common/EditingHistoryControls.tsx";
import type { EditorProps } from "../../viewer/common/Types.ts";
import BuildProgressWrapper from "../BuildProgressWrapper.tsx";
import ChangePreviewModeButton from "../common/ChangePreviewModeButton.tsx";
import BuilderMoreAction from "../common/fields/BuilderMoreAction.tsx";
import useGridStateBuilder from "./hooks/useGridStateBuilder.ts";
import useTrackMeasuresListUpdate from "./hooks/useTrackMeasuresListUpdate.ts";
import EditPreviewComponent from "./preview/EditPreviewComponent.tsx";
import TabularContainer from "./preview/TabularContainer.tsx";
import TableComponent from "./table/TableComponent.tsx";

export default function TabularEditor({ exportAllowed = true, ReportTypeSelector }: EditorProps) {
  const cashFlow = useSelector(selectReportType) === ReportType.CashFlow;
  useTrackMeasuresListUpdate();
  const { onRefreshGrid } = useBiCommonDataContext();

  return (
    <TabularContainer>
      <Stack direction="row" sx={{ flex: 1 }}>
        <PartContainer
          sx={{ maxWidth: "24rem", minWidth: "24rem" }}
          caption={<Box sx={{ pl: "1.3rem" }}>Builder</Box>}
          actions={<BuilderMoreAction />}
        >
          {cashFlow ? (
            <CashFlowBuilder ReportTypeSelector={ReportTypeSelector} />
          ) : (
            <TableComponent ReportTypeSelector={ReportTypeSelector} />
          )}
        </PartContainer>
        <BuildProgressWrapper>
          <PartContainer
            sx={{ borderRight: "unset", p: "1.3rem", pt: 2 }}
            containerSx={{ position: "relative", gap: "1rem" }}
            caption="Preview"
            actions={
              <PreviewActions
                onRefresh={onRefreshGrid}
                ChangePreviewModeButton={<ChangePreviewModeButton />}
                EditingHistory={<EditingHistoryControls />}
              />
            }
          >
            <TabularEditorContainer exportAllowed={exportAllowed} />
          </PartContainer>
        </BuildProgressWrapper>
      </Stack>
    </TabularContainer>
  );
}

function TabularEditorContainer({ exportAllowed = true }: { exportAllowed?: boolean }) {
  const gridState = useGridStateBuilder();
  const cashFlow = useSelector(selectReportType) === ReportType.CashFlow;
  const fields = useSelector(selectTabularFields);
  const settings = useSelector(selectTabularSettingsState);

  const { requestConfig } = useRequestConfigBuilder();
  const dimensions = useSelector(selectDimensions);
  const { requestBuildTabularExport } = useBiApiClientProvider<BiApiClient>();
  if (cashFlow) {
    return (
      <CashFlowReportPreview
        gridState={gridState}
        fields={fields}
        settings={settings}
        exportSource={
          exportAllowed
            ? { mode: "build", requestConfig, dimensions, requestExport: requestBuildTabularExport }
            : undefined
        }
      />
    );
  }
  return (
    <>
      {exportAllowed && (
        <ExcelReportExport
          mode="build"
          requestConfig={requestConfig}
          dimensions={dimensions}
          requestExport={requestBuildTabularExport}
          warning={getTabularExportWarning(gridState)}
        />
      )}
      <EditPreviewComponent gridState={gridState} />
    </>
  );
}
